/**
 * Cuidari MUI theme — round-4 "use MaterialUI components" adoption.
 *
 * Goal: a single themed primitive layer that reads **Airbnb-warm, not Material**.
 * Tokens mirror the hestia-web `[data-brand="cuidari"]` overlay (teal #0d9488
 * primary, coral #f97066 accent, Inter Tight display, system body stack, bumped
 * radii) so MUI components are indistinguishable from the hand-rolled cards
 * already shipped. We deliberately DISABLE the Material tells (ripple, hard
 * elevation shadows, uppercase buttons) and never enable CssBaseline (it would
 * clobber the Tailwind + Metronic global resets).
 *
 * Fonts are bound to the app CSS vars (`--font-sans` / `--font-display`) so MUI
 * text matches every other surface exactly rather than forking to literal Inter.
 */
import { createTheme, type Shadows } from '@mui/material/styles'

const TEAL = '#0d9488'
const TEAL_DARK = '#0f766e'
const TEAL_HOVER = '#0b827a'
const TEAL_LIGHT = '#5eead4'
const TEAL_SOFT = 'rgba(13,148,136,0.08)' // selected/idle tint
const CORAL = '#f97066'
const INK = '#0f172a'
const MUTED = '#64748b'
const DIVIDER = '#e2e8f0'
const PAPER = '#ffffff'
const CANVAS = '#FEFEFE'

const FONT_SANS = 'var(--font-sans), system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
const FONT_DISPLAY = 'var(--font-display, var(--font-sans)), "Inter Tight", system-ui, sans-serif'

// Soft, warm shadow ladder — replaces MUI's harsh grey elevation set.
const SOFT_1 = '0 8px 24px rgba(15,23,42,0.035)'
const SOFT_2 = '0 14px 30px rgba(15,23,42,0.07)'
const SOFT_3 = '0 18px 40px -10px rgba(13,148,136,0.28)'
const softShadows = Array.from({ length: 25 }, (_, i) => {
  if (i === 0) return 'none'
  if (i === 1) return SOFT_1
  if (i === 2) return SOFT_2
  if (i === 3) return SOFT_3
  return SOFT_2
}) as unknown as Shadows

export const cuidariMuiTheme = createTheme({
  cssVariables: false,
  palette: {
    mode: 'light',
    primary: { main: TEAL, dark: TEAL_DARK, light: TEAL_LIGHT, contrastText: '#ffffff' },
    secondary: { main: CORAL, dark: '#e45a4f', contrastText: '#ffffff' },
    success: { main: '#16a34a', contrastText: '#ffffff' },
    warning: { main: '#d97706', contrastText: '#ffffff' },
    error: { main: '#dc2626', contrastText: '#ffffff' },
    info: { main: TEAL, contrastText: '#ffffff' }, // re-pointed off cobalt #2563eb
    background: { default: CANVAS, paper: PAPER },
    text: { primary: INK, secondary: MUTED },
    divider: DIVIDER,
    action: {
      hover: TEAL_SOFT,
      selected: TEAL_SOFT,
      focus: 'rgba(13,148,136,0.16)',
    },
  },
  shape: { borderRadius: 12 },
  shadows: softShadows,
  typography: {
    fontFamily: FONT_SANS,
    h1: { fontFamily: FONT_DISPLAY, fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.015em', lineHeight: 1.2 },
    h2: { fontFamily: FONT_DISPLAY, fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.25 },
    h3: { fontFamily: FONT_DISPLAY, fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h4: { fontFamily: FONT_DISPLAY, fontSize: '1.075rem', fontWeight: 700, lineHeight: 1.35 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700, fontSize: '0.95rem' },
    subtitle1: { color: MUTED, lineHeight: 1.5 },
    subtitle2: { color: MUTED, fontWeight: 600 },
    body1: { lineHeight: 1.55 },
    body2: { lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 700, letterSpacing: 0 },
    overline: { textTransform: 'uppercase', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.06em', color: TEAL },
  },
  components: {
    MuiButtonBase: { defaultProps: { disableRipple: true } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 700,
          padding: '9px 18px',
          transition: 'background-color 160ms ease, box-shadow 160ms ease, transform 120ms ease, border-color 160ms ease',
          '&:active': { transform: 'translateY(0.5px)' },
          '&.Mui-focusVisible': { boxShadow: `0 0 0 3px ${'rgba(13,148,136,0.35)'}` },
        },
        sizeLarge: { padding: '12px 22px', fontSize: '0.975rem', minHeight: 48 },
        sizeSmall: { padding: '6px 12px' },
        containedPrimary: {
          color: '#fff',
          boxShadow: '0 6px 16px -6px rgba(13,148,136,0.55)',
          '&:hover': { backgroundColor: TEAL_HOVER, boxShadow: '0 10px 22px -8px rgba(13,148,136,0.6)' },
        },
        outlinedPrimary: {
          borderColor: 'rgba(13,148,136,0.4)',
          '&:hover': { borderColor: TEAL, backgroundColor: TEAL_SOFT },
        },
        textPrimary: { '&:hover': { backgroundColor: TEAL_SOFT } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'background-color 140ms ease, color 140ms ease',
          '&.Mui-focusVisible': { boxShadow: `0 0 0 3px rgba(13,148,136,0.35)` },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 16 },
        outlined: { borderColor: DIVIDER },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${DIVIDER}`,
          boxShadow: SOFT_1,
          backgroundImage: 'none',
          transition: 'box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease',
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          '&:hover': { backgroundColor: 'transparent' },
          '& .MuiCardActionArea-focusHighlight': { background: 'transparent' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 9999, fontWeight: 600, height: 26 },
        sizeSmall: { height: 22, fontSize: '0.72rem' },
        label: { paddingLeft: 10, paddingRight: 10 },
        outlined: { borderColor: DIVIDER },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { width: 46, height: 28, padding: 0, display: 'inline-flex' },
        switchBase: {
          padding: 4,
          '&.Mui-checked': { transform: 'translateX(18px)', color: '#fff' },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: TEAL, opacity: 1 },
          '&.Mui-focusVisible + .MuiSwitch-track': { boxShadow: `0 0 0 3px rgba(13,148,136,0.35)` },
        },
        thumb: { width: 20, height: 20, boxShadow: '0 1px 3px rgba(15,23,42,0.2)' },
        track: { borderRadius: 999, backgroundColor: '#cbd5e1', opacity: 1, transition: 'background-color 160ms ease' },
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#fff',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: DIVIDER },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TEAL, borderWidth: 2 },
        },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { '&.Mui-focused': { color: TEAL_DARK } } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1e293b',
          borderRadius: 10,
          fontSize: '0.78rem',
          fontWeight: 500,
          padding: '7px 11px',
          boxShadow: SOFT_2,
        },
        arrow: { color: '#1e293b' },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: { backgroundColor: TEAL, height: 3, borderRadius: 3 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
          color: MUTED,
          '&.Mui-selected': { color: TEAL_DARK },
          '&.Mui-focusVisible': { backgroundColor: TEAL_SOFT },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderColor: DIVIDER,
          color: MUTED,
          borderRadius: 9999,
          padding: '6px 14px',
          '&.Mui-selected': { backgroundColor: TEAL_SOFT, color: TEAL_DARK, borderColor: 'rgba(13,148,136,0.4)' },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(13,148,136,0.14)' },
        },
      },
    },
    MuiToggleButtonGroup: { styleOverrides: { grouped: { margin: 0 } } },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, height: 8, backgroundColor: '#e2e8f0' },
        bar: { borderRadius: 999, backgroundColor: TEAL },
      },
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: { root: { backgroundColor: 'rgba(148,163,184,0.18)' }, rounded: { borderRadius: 12 } },
    },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 20, boxShadow: SOFT_3 } } },
    MuiDrawer: { styleOverrides: { paper: { backgroundImage: 'none' } } },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 14, boxShadow: SOFT_2, border: `1px solid ${DIVIDER}`, marginTop: 6 },
        list: { padding: 6 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 9,
          fontSize: '0.9rem',
          padding: '8px 10px',
          '&:hover': { backgroundColor: TEAL_SOFT },
          '&.Mui-selected': { backgroundColor: TEAL_SOFT },
        },
      },
    },
    MuiSnackbar: { styleOverrides: { root: { '& .MuiPaper-root': { borderRadius: 12 } } } },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, fontWeight: 500, boxShadow: SOFT_2, alignItems: 'center' },
        filledSuccess: { backgroundColor: '#0d9488' },
        standardSuccess: { backgroundColor: 'rgba(13,148,136,0.1)', color: TEAL_DARK },
      },
    },
    MuiAvatar: { styleOverrides: { root: { fontWeight: 700, backgroundColor: TEAL } } },
    MuiBadge: { styleOverrides: { badge: { fontWeight: 700, fontSize: '0.66rem' } } },
    MuiDivider: { styleOverrides: { root: { borderColor: DIVIDER } } },
    MuiBreadcrumbs: { styleOverrides: { separator: { color: '#94a3b8', marginLeft: 6, marginRight: 6 } } },
  },
})
